import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { AddressComponent } from '../../../../shared/components/address/address.component';
import { SectionsService } from '../../../../services/sections.service';
import { BaseComponent } from '../../../../shared/utils/base.component';
import { ListTableComponent } from '@ppl/ui';
import { LeadDetailsService } from '../../../../services/lead-details.service';
import { performValidation } from '../../../../shared/utils/validation.util';
import {
  STATIC_DATA,
  STATIC_JSON_DATA_PATH,
} from '../../../../shared/constants/static-data.constants';
import { NgZorroModule } from '../../../../shared/modules/ng-zorro/ng-zorro.module';
import { CommonModule } from '@angular/common';
import { LoggerService } from '../../../../shared/services/logger.service';
import {
  LeadDetailCommunicationModel,
  LeadDetailCommunicationUpdadeModel,
} from '../../../../interfaces/lead-detail.model';
import { TableRowActionsModel } from '../../../../shared/interfaces/list.interface';
import { TableRowActions } from '../../../../shared/interfaces/actions';
import { AuthorizeDirective } from '../../../../shared/authorization/authorize.directive.';
import { ButtonComponent } from '@ppl/ui';
import { NzAriaLabelDirective } from '../../../../shared/directives/nz-aria-label.directive';
import { AuthorizationUtility } from '../../../../shared/authorization/auth.utility';
import { UserService } from '../../../../services/user.service';
import { StorageService } from '../../../../services/storage.service';
import { DropdownSelectComponent } from '@ppl/ui';

@Component({
  selector: 'ppl-lead-detail-comm-methods',
  standalone: true,
  imports: [
    NgZorroModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    AddressComponent,
    ListTableComponent,
    AuthorizeDirective,
    ButtonComponent,
    NzAriaLabelDirective,
    DropdownSelectComponent,
  ],
  templateUrl: './lead-detail-comm-methods.component.html',
  styleUrl: './lead-detail-comm-methods.component.css',
})
export class LeadDetailCommMethodsComponent
  extends BaseComponent
  implements OnInit, OnChanges {
  mode = 'read';
  filledInfo!: any;
  contactForm!: FormGroup;
  cols: any;
  gridData: any[] = [];
  communicationType: any;
  copyOfGridData: any = [];
  steps: any;
  entry = 'base';
  target!: LeadDetailCommunicationUpdadeModel;
  selectedLead = 0;
  actionButtons: TableRowActionsModel = new TableRowActions();
  approvedStatus: string = STATIC_DATA.APPROVED;
  contactMethodType: string | null = null;
  leadStatus = '';
  isSectionEditable: boolean = false;
  isSectionDeletable: boolean = false;
  phoneNUmberError = false;
  createdContact: any[] = [];
  selectedCommValue: string | undefined | null = null;
  enrollmentType: any = '';
  contactMethodMap: Record<string, string> = {
    altphone: 'Telephone Number',
    telephone: 'Telephone Number',
    email: 'Email Address',
    mobile: 'Mobile Number',
    text: 'Mobile Number'
  };
  showPreferredDeleteMessageInEdit: boolean = false;
  showPreferredDeleteMessageInDelete: boolean = false;
  preferredDeleteMessage: string = 'There should be at least one preferred contact method against the user';

  @Input() pageId = '';
  @Input() tabId = '';
  @Input() programCode = '';
  @Input() readOnly = false;
  @Input() set sectionData(data: string) {
    this.steps = data;
  }

  @Output() leadUpdated = new EventEmitter<boolean>();

  constructor(
    private fb: FormBuilder,
    private sectionService: SectionsService,
    private leadDetailsService: LeadDetailsService,
    private cdr: ChangeDetectorRef,
    private logger: LoggerService,
    private authUtility: AuthorizationUtility,
    private userService: UserService,
    private storageService: StorageService
  ) {
    super();
  }

  ngOnInit(): void {
    this.entry = this.userService.getLeadEntry() ?? '';
    this.communicationType =
      this.lookupService.getLookupValue('Communication_Type');
    this.isSectionEditable = this.readOnly ? false : this.authUtility.isSectionEditable(this.pageId, this.tabId, 'CommunicationMethods');
    this.isSectionDeletable = this.readOnly ? false : this.authUtility.isSectionDeletable(this.pageId, this.tabId, 'CommunicationMethods');
    this.enrollmentType = this.sectionService.getEnrollmentType();
    this.selectedLead = JSON.parse(
      this.userService.getSelectedLead()!
    );
    this.leadDetailsService.getLeadStatus().subscribe((status: string) => {
      this.leadStatus = status;
    });
    this.createFormGroup();
    this.getFromData();
    this.leadDetailsService.getUpdatedLeadData().subscribe((data) => {
      this.gridData = data?.responsedata?.communicationData?.contactMethods;
      this.copyAndTransformGridData(this.gridData);
      this.communicationType = this.communicationType.map((item: any) => {
        return {
          ...item,
          disabled: this.shoulddisableContactMethod(item),
        };
      });
    });
  }

  shoulddisableContactMethod = (option: any) => this.createdContact.includes(option.lkpValueCode);

  get allContactMethodsUsed(): boolean {
    return this.communicationType?.length > 0 && this.communicationType.every((item: any) => this.createdContact.includes(item.lkpValueCode));
  }

  ngOnChanges(changes: SimpleChanges) {
    try {
      if (changes['sectionData'] && changes['sectionData'].currentValue) {
        this.secData = this.steps['communication_data_contact_methods'];
      }
    } catch (e) {
      this.logger.debug(
        'Error in getting section data and updating latest info',
        e
      );
    }
  }

  onContactMethodSelection(event: any) {
    this.contactMethodType = this.contactMethodMap[event.trim().toLowerCase()];
  }

  limitDigits(event: any): void {
    const selectedValue = this.contactForm?.get('contactMethod')?.value;
    if (selectedValue == 'mobile' || selectedValue === 'altPhone') {
      let value = event.target.value.replace(/\D/g, '');
      if (value.length > 10) {
        value = value.slice(0, 10);

        event.target.value = value;
        this.contactForm.get(selectedValue)?.setValue(value);
      }
    }

  }

  createFormGroup() {
    this.contactForm = this.fb.group({
      contactMethod: [''],
      leadCommuniDetailsId: [''],
      id: [],
      value: [],
      preferred: [true],
    });
  }

  async getFromData() {
    //table column information
    try {
      if (this.entry) {
        const tempCols = await this.sectionService.getJsonConfigurationData(
          STATIC_JSON_DATA_PATH.LEAD_DETAILS_CONTACT_LIST_HEADERS,
          this.entry
        );

        this.cols = tempCols?.contactMethods?.columns;
      }
    } catch (e) {
      this.logger.debug('Error in getting table headers', e);
    }
  }

  cancelEdit() {
    this.mode = 'read';
    this.contactMethodType = null;
    this.showPreferredDeleteMessageInDelete = false;
    this.showPreferredDeleteMessageInEdit = false;
    this.validationErrors = {};
    this.phoneNUmberError = false;
    this.contactForm.reset();
    setTimeout(() => this.cdr.detectChanges(), 1000);
  }

  action(event: any) {
    const selectedCommId = this.extractId(event?.data);

    const selectedData = this.gridData.find((row: any) => {
      if ([row.communiDetailsId, row.leadCommuniDetailsId, row.referralCommuniDetailsId, row.participantCommuniDetailsId].includes(selectedCommId)) {
        return row;
      }
    });

    if (event && event.action === 'edit') {
      this.mode = 'edit';
      this.contactMethodType = this.contactMethodMap[event?.data?.contactMethod?.trim().toLowerCase()];

      if (selectedData) {
        this.contactForm.patchValue(selectedData);

        if ([STATIC_DATA.REFERRAL, STATIC_DATA.PARTICIPANT_ENROLLMENT, STATIC_DATA.PROVIDER_ENROLLMENT].includes(this.entry)) {
          this.contactForm.patchValue({
            leadCommuniDetailsId: selectedCommId,
            id: this.selectedLead,
          });
        }
      }
    }

    if (event && event.action === 'delete') {
      if (selectedData) {
        const isPreferred = selectedData.preferred === true || selectedData.preferred === 'Yes';

        if (isPreferred) {
          const preferredRecords = this.copyOfGridData.filter((contact: any) =>
            contact.preferred === 'Yes' || contact.preferred === true
          );

          if (preferredRecords.length <= 1) {
            this.showPreferredDeleteMessageInDelete = true;
            return;
          }
        }

        if (this.copyOfGridData.length <= 1) {
          this.showPreferredDeleteMessageInDelete = true;
          return;
        }

        this.delete(selectedData);
      }
    }
  }

  closePreferredDeleteMessage(): void {
    this.showPreferredDeleteMessageInDelete = false;
    this.showPreferredDeleteMessageInEdit = false;
  }

  getRowActions(item: any) {
    if (((this.entry === STATIC_DATA.PARTICIPANT_ENROLLMENT && (
      [
        STATIC_DATA.ASSOCIATION_AUTHORIZED_REPRESENTATIVE_TYPE,
        STATIC_DATA.ASSOCIATION_EMPLOYER_TYPE
      ].includes(item?.providerType))
    ) || this.leadStatus === this.approvedStatus)) {
      return { edit: false, delete: false };
    }

    const isEmployer = Array.isArray(this.enrollmentType) && this.enrollmentType.includes('employer');
    if (isEmployer && item?.contactMethod === 'altPhone') {
      return { edit: this.isSectionEditable, delete: false };
    }

    return { edit: this.isSectionEditable, delete: this.isSectionDeletable };
  }

  copyAndTransformGridData(gridData: any[]) {
    this.createdContact = [];
    if (gridData) {
      const copyGridData = JSON.parse(JSON.stringify(gridData));

      this.copyOfGridData = copyGridData.map((item: any) => ({
        ...item,
        preferred: item.preferred ? 'Yes' : 'No',
        isSMSConstentGiven: item.isSMSConstentGiven ? 'Yes' : 'No',
        contactMethod: super.getLookupValue(this.communicationType, item.contactMethod) || item.contactMethod,
        modifiedDate: super.formatDateString(item.modifiedDate),
        leadCommuniDetailsId: item.leadCommuniDetailsId || item.referralCommuniDetailsId || item.communiDetailsId,
        actions: this.getRowActions(item)
      }));
      copyGridData.forEach((item: any) => {
        if (!item.providerType || item.providerType === 'Participant') {
          this.createdContact.push(item.contactMethod);
        }
      });
      this.copyOfGridData = [...this.copyOfGridData];
    } else {
      this.copyOfGridData = [];
    }
  }

  add() {
    this.mode = 'add';
    this.contactForm.reset();
    this.contactForm.controls['id'].setValue(this.selectedLead);
  }

  save() {
    const data = { ...this.contactForm.value };
    this.phoneNUmberError = false;
    try {
      this.validationErrors = performValidation(data, this.secData, 'Save', undefined, undefined, this.sectionService.getFlow(), this.sectionService.getEnrollmentType());
    } catch (e) {
      this.logger.debug('Error in validations', e);
    }

    if (data.contactMethod === 'mobile' || data.contactMethod === 'altPhone') {
      if (data.contactMethod === 'mobile') {
        data.isSMSConstentGiven = true;
      }

      const phoneNumber = data.value;
      if (!/^\d{10}$/.test(phoneNumber)) {
        this.phoneNUmberError = true;
      }

      if (/^(.)\1{9}$/.test(phoneNumber)) {
        this.phoneNUmberError = true;
      }

      const unrealisticPatterns = [
        '1234567890', '9876543210', '0123456789', '1234567890'
      ];
      if (unrealisticPatterns.includes(phoneNumber)) {
        this.phoneNUmberError = true;
      }
      const tollFreePrefixes = ['800', '888', '877', '866', '855', '844', '833', '822'];
      const reservedPrefixes = ['911', '411', '611', '811', '555'];
      if (tollFreePrefixes.includes(phoneNumber.slice(0, 3)) || reservedPrefixes.includes(phoneNumber.slice(0, 3))) {
        this.phoneNUmberError = true;
      }

      const areaCode = phoneNumber.slice(0, 3);
      const centralOfficeCode = phoneNumber.slice(3, 6);
      if (areaCode[0] === '0' || areaCode[0] === '1' || centralOfficeCode[0] === '0' || centralOfficeCode[0] === '1') {
        this.phoneNUmberError = true;
      }
    }

    if (this.mode === 'edit' && !data.preferred) {
      const preferredRecords = this.copyOfGridData.filter((contact: any) =>
        contact.preferred === 'Yes' || contact.preferred === true
      );

      const editingId = data.leadCommuniDetailsId;
      const isEditingPreferred = preferredRecords.some((contact: any) =>
        contact.participantCommuniDetailsId === editingId ||
        contact.leadCommuniDetailsId === editingId ||
        contact.referralCommuniDetailsId === editingId ||
        contact.communiDetailsId === editingId
      );

      if (isEditingPreferred && preferredRecords.length <= 1) {
        this.showPreferredDeleteMessageInEdit = true;
        return;
      }
    }

    if (Object.keys(this.validationErrors).length === 0 && !this.phoneNUmberError) {
      this.target = this.preparePayload(data);
      this.leadDetailsService
        .createUpdateLeadCommunicationMethods(this.target, this.entry)
        .subscribe(
          {
            next: () => {
              this.logger.info('Contact method saved successfully');
              this.leadUpdated.emit(true);
              this.cancelEdit();
            },
            error: () => {
              this.leadUpdated.emit(true);
              this.cancelEdit();
            }
          }
        );
    }
  }


  delete(commMethod: any) {
    const id = this.extractId(commMethod)
    this.leadDetailsService
      .deleteLeadCommunicationMethod(id, this.entry, this.programCode)
      .subscribe(() => {
        this.logger.info('Contact method deleted successfully');
        this.leadUpdated.emit(true);
        /* this.getLatestLeadData(); */
      });
  }

  preparePayload(
    source: LeadDetailCommunicationModel,

  ): LeadDetailCommunicationUpdadeModel {
    const communiDetailsId: number | null = source.leadCommuniDetailsId ?? null;
    const existingContactMethod = this.gridData?.find(
      (row) => {
        if (
          communiDetailsId && [row.communiDetailsId, row.leadCommuniDetailsId, row.referralCommuniDetailsId, row.participantCommuniDetailsId].includes(communiDetailsId)
        ) {
          return row;
        }
      }
    );
    return {
      id: this.selectedLead,
      programCode: this.programCode,
      communiDetailsId,
      referralId: this.storageService.getReferralConsumerId(),
      communicationMethod: {
        ...source,
        modifiedDate: existingContactMethod?.modifiedDate
      },
    };
  }


  extractId(commMethod: any) {
    let id = commMethod?.leadCommuniDetailsId;
    if (this.entry === STATIC_DATA.REFERRAL) {
      id = commMethod.referralCommuniDetailsId;
    }
    if (this.entry === STATIC_DATA.PARTICIPANT_ENROLLMENT) {
      id = commMethod.participantCommuniDetailsId
    }
    if (this.entry === STATIC_DATA.PROVIDER_ENROLLMENT) {
      id = commMethod.communiDetailsId
    }
    return id;
  }

}
